/**
 * PCE Product Preloader
 * Intelligently preloads all collection products in the background
 * for instant filtering and pagination
 *
 * Features:
 * - Background preloading of all pages after initial page load
 * - Caches products in memory for instant access
 * - Integrates with filters for instant show/hide
 * - Makes "Load More" instant with cached products
 * - Progressive enhancement - works without breaking existing functionality
 */

class PCEProductPreloader {
    constructor() {
        this.container = document.querySelector('[data-pce-pagination]');
        if (!this.container) return;

        this.productsContainer = this.container.querySelector('[data-products-container]');
        if (!this.productsContainer) return;

        // Configuration
        this.targetTag = this.productsContainer.dataset.targetTag;
        this.currentPage = parseInt(this.productsContainer.dataset.currentPage) || 1;
        this.totalPages = parseInt(this.productsContainer.dataset.totalPages) || 1;
        this.baseUrl = this.productsContainer.dataset.baseUrl || window.location.pathname;

        // Cache storage
        this.cachedProducts = new Map(); // pageNum => product elements
        this.allProducts = []; // All products in order
        this.visibleProductCount = 0; // Track how many products are currently visible
        this.preloadComplete = false;
        this.preloading = false;

        // Parser for HTML
        this.parser = new DOMParser();

        // Store initial products
        this.storeInitialProducts();

        // Start preloading remaining pages after a delay
        if (this.totalPages > 1) {
            // Delay preloading to prioritize initial page interaction
            setTimeout(() => this.startPreloading(), 1000);
        }

        // Expose methods for integration
        this.exposeAPI();
    }

    /**
     * Store initially loaded products
     */
    storeInitialProducts() {
        const initialProducts = Array.from(this.productsContainer.querySelectorAll('.product-item'));
        // Store products for the current page (not always page 1)
        this.cachedProducts.set(this.currentPage, initialProducts);
        this.allProducts = [...initialProducts];
        this.visibleProductCount = initialProducts.length;

        // Mark initial products with their page number
        initialProducts.forEach(product => {
            product.dataset.pageNum = this.currentPage;
        });

        console.log(`PCE Preloader: Stored ${initialProducts.length} products from page ${this.currentPage}`);
    }

    /**
     * Start background preloading of all remaining pages
     */
    async startPreloading() {
        if (this.preloading || this.preloadComplete) return;

        this.preloading = true;
        console.log('PCE Preloader: Starting background preload...');

        // Add subtle loading indicator
        this.addPreloadIndicator();

        // Preload ALL pages (except the current one)
        for (let page = 1; page <= this.totalPages; page++) {
            if (this.cachedProducts.has(page)) continue; // Skip already cached pages

            await this.preloadPage(page);

            // Small delay between requests to avoid overwhelming server
            await this.delay(200);
        }

        this.preloading = false;
        this.preloadComplete = true;

        // Remove loading indicator
        this.removePreloadIndicator();

        console.log(`PCE Preloader: Preloading complete! Total products: ${this.allProducts.length}`);

        // Notify other components
        this.notifyPreloadComplete();
    }

    /**
     * Preload a specific page
     */
    async preloadPage(pageNum) {
        try {
            const url = `${this.baseUrl}?page=${pageNum}`;
            const response = await fetch(url, {
                headers: {
                    'X-Requested-With': 'XMLHttpRequest'
                }
            });

            if (!response.ok) {
                console.warn(`PCE Preloader: Failed to load page ${pageNum}`);
                return;
            }

            const html = await response.text();
            const doc = this.parser.parseFromString(html, 'text/html');

            // Extract products
            const loadedProductsContainer = doc.querySelector('[data-products-container]');
            if (!loadedProductsContainer) return;

            const productItems = Array.from(loadedProductsContainer.children).filter(
                child => !child.hasAttribute('data-sparse-page') &&
                        child.classList.contains('product-item')
            );

            if (productItems.length > 0) {
                // Clone and store products
                const clonedProducts = productItems.map(item => item.cloneNode(true));

                // Mark as preloaded (hidden by default)
                clonedProducts.forEach(product => {
                    product.style.display = 'none';
                    product.dataset.preloaded = 'true';
                    product.dataset.pageNum = pageNum;
                });

                // Store in cache
                this.cachedProducts.set(pageNum, clonedProducts);
                this.allProducts.push(...clonedProducts);

                console.log(`PCE Preloader: Cached ${productItems.length} products from page ${pageNum}`);
            }

        } catch (error) {
            console.error(`PCE Preloader: Error loading page ${pageNum}:`, error);
        }
    }

    /**
     * Get cached products for a specific page
     */
    getCachedProducts(pageNum) {
        return this.cachedProducts.get(pageNum) || [];
    }

    /**
     * Get all cached products
     */
    getAllProducts() {
        return this.allProducts;
    }

    /**
     * Get items per page from section settings
     */
    getItemsPerPage() {
        try {
            const section = this.container.closest('[data-section-type="collection"]');
            if (section) {
                const settings = JSON.parse(section.dataset.sectionSettings || '{}');
                return settings.itemsPerPage || settings.defaultProductsPerPage || 24;
            }
        } catch (e) {
            console.warn('PCE Preloader: Could not read items per page from settings, defaulting to 24');
        }
        return 24;
    }

    /**
     * Load more products instantly from cache
     */
    loadMoreFromCache() {
        const itemsPerPage = this.getItemsPerPage();
        const nextPage = Math.floor(this.visibleProductCount / itemsPerPage) + 2;
        const products = this.getCachedProducts(nextPage);

        if (products.length === 0) {
            console.log('PCE Preloader: No cached products for next page');
            return false;
        }

        // Append products to container
        products.forEach(product => {
            product.style.display = ''; // Make visible
            this.productsContainer.appendChild(product);
            this.visibleProductCount++;
        });

        // Trigger re-filter if filters are active
        if (window.PCETemplate) {
            const pceTemplate = this.container.querySelector('pce-template');
            if (pceTemplate && pceTemplate.updateProductList) {
                pceTemplate.updateProductList();
            }
        }

        console.log(`PCE Preloader: Loaded ${products.length} products from cache`);
        return true;
    }

    /**
     * Apply filters to all products (including preloaded)
     */
    applyFiltersToAll(filterCallback) {
        let visibleCount = 0;

        this.allProducts.forEach(product => {
            const shouldShow = filterCallback(product);

            if (shouldShow) {
                // Only show if it's either visible or should be made visible
                if (!product.dataset.preloaded || product.parentElement === this.productsContainer) {
                    product.style.display = '';
                    product.removeAttribute('hidden');
                    visibleCount++;
                }
            } else {
                product.style.display = 'none';
                product.setAttribute('hidden', '');
            }
        });

        return visibleCount;
    }

    /**
     * Add subtle loading indicator
     */
    addPreloadIndicator() {
        if (document.getElementById('pce-preload-indicator')) return;

        const indicator = document.createElement('div');
        indicator.id = 'pce-preload-indicator';
        indicator.innerHTML = `
            <style>
                #pce-preload-indicator {
                    position: fixed;
                    bottom: 20px;
                    right: 20px;
                    background: rgba(0, 0, 0, 0.7);
                    color: white;
                    padding: 8px 12px;
                    border-radius: 4px;
                    font-size: 12px;
                    z-index: 1000;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    opacity: 0.8;
                    transition: opacity 0.3s;
                }
                #pce-preload-indicator.complete {
                    background: rgba(0, 128, 0, 0.7);
                }
                .pce-spinner {
                    width: 12px;
                    height: 12px;
                    border: 2px solid rgba(255, 255, 255, 0.3);
                    border-top-color: white;
                    border-radius: 50%;
                    animation: pce-spin 1s linear infinite;
                }
                @keyframes pce-spin {
                    to { transform: rotate(360deg); }
                }
            </style>
            <div class="pce-spinner"></div>
            <span>Loading all products...</span>
        `;
        document.body.appendChild(indicator);
    }

    /**
     * Remove loading indicator
     */
    removePreloadIndicator() {
        const indicator = document.getElementById('pce-preload-indicator');
        if (indicator) {
            indicator.classList.add('complete');
            indicator.innerHTML = indicator.innerHTML.replace('class="pce-spinner"', 'style="display:none"');
            indicator.querySelector('span').textContent = 'All products loaded!';

            setTimeout(() => {
                indicator.style.opacity = '0';
                setTimeout(() => indicator.remove(), 300);
            }, 2000);
        }
    }

    /**
     * Notify other components that preloading is complete
     */
    notifyPreloadComplete() {
        // Dispatch custom event
        this.container.dispatchEvent(new CustomEvent('pce:preloadComplete', {
            detail: {
                totalProducts: this.allProducts.length,
                totalPages: this.totalPages
            }
        }));

        // Update PCEPagination if it exists
        if (window.PCEPagination) {
            const pagination = window.pcePaginationInstance;
            if (pagination && pagination.onPreloadComplete) {
                pagination.onPreloadComplete();
            }
        }

        // Update PCETemplate if it exists
        if (window.PCETemplate) {
            const pceTemplate = this.container.querySelector('pce-template');
            if (pceTemplate && pceTemplate.onPreloadComplete) {
                pceTemplate.onPreloadComplete();
            }
        }
    }

    /**
     * Expose API for other components
     */
    exposeAPI() {
        // Make preloader globally accessible
        window.PCEProductPreloader = PCEProductPreloader;
        window.pcePreloader = this;

        // Add methods to window for easy access
        window.pcePreloader.isPreloadComplete = () => this.preloadComplete;
        window.pcePreloader.getProgress = () => ({
            loaded: this.cachedProducts.size,
            total: this.totalPages,
            percentage: (this.cachedProducts.size / this.totalPages) * 100
        });
    }

    /**
     * Utility: delay function
     */
    delay(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }
}

/**
 * Initialize preloader
 */
function initPCEPreloader() {
    // Only initialize on PCE pages
    if (document.querySelector('[data-pce-pagination]')) {
        new PCEProductPreloader();
    }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPCEPreloader);
} else {
    initPCEPreloader();
}

// Export for use in other scripts
window.PCEProductPreloader = PCEProductPreloader;